import { PayloadAction } from '@reduxjs/toolkit';
import createAppSlice from 'app/createAppSlice';
import type {
  ChannelStatus,
  ChannelType,
  ChannelValue,
  NotificationChannel,
  NotificationSettingsSlice as NotificationSettingsSliceType,
} from './types';

const initialState: NotificationSettingsSliceType = {
  channels: null,
};

const notificationSettingsSlice = createAppSlice({
  name: 'notificationSettings',
  initialState,
  reducers: {
    /**
     * The only write that flips the loaded gate. Data-only by design — pending and error live
     * in `apiSlice` (rule 5), and the `null` → array transition is a single atomic commit so
     * nothing can observe a "loaded but empty" frame ahead of it (rule 7).
     */
    setChannels(state, action: PayloadAction<NotificationChannel[]>) {
      state.channels = action.payload;
    },

    /**
     * Flips one channel's status after its API call has resolved — never before. Angular set
     * the store optimistically and reverted on cancel; rule 6 forbids that, and Figma agrees
     * (the switch behind the enrolment dialog stays off until verification completes).
     *
     * Acts only on the channel named, which is what stops the deactivate defect in BRD §14.3
     * from reappearing once a third channel exists (FR-034).
     */
    setChannelStatus(
      state,
      action: PayloadAction<{ channel: ChannelType; status: ChannelStatus }>,
    ) {
      const target = state.channels?.find(
        (item) => item.channel === action.payload.channel,
      );
      if (target) {
        target.status = action.payload.status;
      }
    },

    /**
     * Records a channel's new destination — a newly enrolled number, or a saved email address —
     * so the card renders it without waiting for a re-read of the preferences endpoint.
     */
    setChannelValue(
      state,
      action: PayloadAction<{
        channel: ChannelType;
        value: Partial<ChannelValue>;
      }>,
    ) {
      const target = state.channels?.find(
        (item) => item.channel === action.payload.channel,
      );
      if (target) {
        target.value = { ...target.value, ...action.payload.value };
      }
    },
  },
});

export const { setChannels, setChannelStatus, setChannelValue } =
  notificationSettingsSlice.actions;

export default notificationSettingsSlice.reducer;
